<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Tags Management</h1>
        <span class="page-subtitle">{{ items.length }} tags</span>
      </div>
      <div class="page-header__right">
        <translator
            :items="items"
            :keys="translateKeys"
            model="Tag"
        >
          <template #default="{ clicked }">
            <button class="btn-secondary-action" @click="clicked">
              <i class="fa fa-language"></i> Translations
            </button>
          </template>
        </translator>
        <button class="btn-primary-action" @click="showCreateModal">
          <i class="fa fa-plus"></i> Create Tag
        </button>
      </div>
    </div>

    <!-- ── Tags Grid ── -->
    <div class="tags-grid">
      <div
          class="tag-card"
          v-for="item in items"
          :key="item.id"
          :style="getPhotoStyle(item)"
          @click="showUpdateModal(item)"
      >
        <div class="tag-card__overlay">
          <div class="tag-card__header">
            <span :class="['tag-status', item.related_attributes?.length > 0 ? 'tag-status--active' : 'tag-status--inactive']">
              {{ item.related_attributes?.length > 0 ? 'Active' : 'Inactive' }}
            </span>
          </div>
          <div class="tag-card__body">
            <div class="tag-card__title">{{ item.title }}</div>
            <div class="tag-card__attrs" v-if="item.related_attributes && item.related_attributes.length">
              <div class="tag-card__attrs-label">Related to:</div>
              <div
                  v-for="attr in item.related_attributes"
                  :key="attr.id"
                  class="tag-card__attr"
              >{{ attr.value }}</div>
            </div>
          </div>
          <div class="tag-card__footer">
            <i class="fa fa-pencil"></i> Edit
          </div>
        </div>
      </div>
    </div>

    <!-- ── Create Tag Modal ── -->
    <modal name="create-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Create Tag</span>
          <button class="dd-modal__close" @click="$modal.hide('create-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-field mb-3">
            <label class="filter-label">Type <span class="required">*</span></label>
            <select v-model="createForm.type" class="dd-input" @change="filterAttributes('create')">
              <option value="">Select type...</option>
              <option value="occasion">Occasion</option>
              <option value="recipient">Recipient</option>
            </select>
          </div>
          <div class="form-field mb-3">
            <label class="filter-label">Title <span class="required">*</span></label>
            <input v-model="createForm.title" type="text" class="dd-input" placeholder="Tag title" required />
          </div>
          <div class="form-field mb-3" v-if="createForm.photo">
            <div class="photo-uploaded">
              <i class="fa fa-check-circle"></i> Photo uploaded
            </div>
          </div>
          <div class="form-field">
            <label class="filter-label">Related Connect Attributes</label>
            <multiselect
                v-model="selectedAttributes"
                :options="attributes"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Pick attributes..."
                label="value"
                track-by="id"
                :preselect-first="false"
                class="dd-multiselect"
            ></multiselect>
          </div>
          <ul class="error-list" v-if="createForm.errors && createForm.errors.length">
            <li v-for="error in createForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('create-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="createTag">
            <i class="fa fa-plus"></i> Create
          </button>
        </div>
      </div>
    </modal>

    <!-- ── Update Tag Modal ── -->
    <modal name="update-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Edit Tag</span>
          <button class="dd-modal__close" @click="$modal.hide('update-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-field mb-3">
            <label class="filter-label">Type</label>
            <select v-model="updateForm.type" class="dd-input" @change="filterAttributes('update')">
              <option value="occasion">Occasion</option>
              <option value="recipient">Recipient</option>
            </select>
          </div>
          <div class="form-field mb-3">
            <label class="filter-label">Title <span class="required">*</span></label>
            <input v-model="updateForm.title" type="text" class="dd-input" placeholder="Tag title" required />
          </div>
          <div class="form-field mb-3" v-if="updateForm.photo">
            <div class="photo-uploaded">
              <i class="fa fa-check-circle"></i> Photo uploaded
            </div>
          </div>
          <div class="form-field">
            <label class="filter-label">Related Connect Attributes</label>
            <multiselect
                v-model="selectedAttributes"
                :options="attributes"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Pick attributes..."
                label="value"
                track-by="id"
                :preselect-first="false"
                class="dd-multiselect"
            ></multiselect>
          </div>
          <ul class="error-list" v-if="updateForm.errors && updateForm.errors.length">
            <li v-for="error in updateForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-delete" @click.prevent="deleteTag">
            <i class="fa fa-trash"></i> Delete
          </button>
          <div style="flex:1"></div>
          <button class="btn-cancel" @click="$modal.hide('update-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="updateTag">
            <i class="fa fa-check"></i> Update
          </button>
        </div>
      </div>
    </modal>

  </div>
</template>

<script>
import { config } from "../helpers";
import multiselect from "vue-multiselect";
import Translator from "@/components/Translator.vue";

export default {
  name: "Tags",
  components: { Translator, multiselect },
  data() {
    return {
      url: "",
      items: [],
      attributes: [],
      selectedAttributes: [],
      createForm: { type: "", title: "", slug: "", description: "", photo: "", errors: [], attributes: [] },
      updateForm: { id: null, type: "", title: "", slug: "", description: "", photo: "", errors: [], attributes: [] },
      translateKeys: [{ key: "value", type: "text" }],
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT");
    this.loadData();
    this.getAttributes();
  },

  methods: {
    loadData() {
      axios.get(`${this.url}/admin/tags`).then((r) => { this.items = r.data.data; });
    },

    getAttributes() {
      axios.get(`${this.url}/admin/attributes`).then((r) => { this.attributes = r.data.data; });
    },

    filterAttributes(action) {
      const type = action === "update" ? this.updateForm.type : this.createForm.type;
      const key  = type.charAt(0).toUpperCase() + type.slice(1) + "-Label";
      this.attributes = this.attributes.filter((a) => a.key === key);
    },

    createTag(event) {
      event.preventDefault();
      axios
          .post(`${this.url}/admin/tags`, {
            type:       this.createForm.type,
            title:      this.createForm.title,
            slug:       this.createForm.slug,
            photo:      this.createForm.photo,
            attributes: this.selectedAttributes.map(({ id, value }) => ({ id, value })),
          })
          .then(() => { this.loadData(); this.$modal.hide("create-modal"); })
          .catch((e) => { this.createForm.errors = Object.values(e.response.data.errors).flat(); });
    },

    updateTag(event) {
      event.preventDefault();
      this.updateForm.attributes = this.selectedAttributes.map(({ id, value }) => ({ id, value }));
      axios
          .put(`${this.url}/admin/tags/${this.updateForm.id}`, {
            type:        this.updateForm.type,
            title:       this.updateForm.title,
            slug:        this.updateForm.slug,
            description: this.updateForm.description,
            photo:       this.updateForm.photo,
            attributes:  this.updateForm.attributes,
          })
          .then(() => { this.loadData(); this.$modal.hide("update-modal"); })
          .catch((e) => { this.updateForm.errors = Object.values(e.response.data.errors).flat(); });
    },

    deleteTag(event) {
      event.preventDefault();
      if (!confirm("Do you want to delete this tag?")) return;
      axios.delete(`${this.url}/admin/tags/${this.updateForm.id}`).then(() => location.reload());
    },

    showCreateModal() {
      this.selectedAttributes = [];
      this.createForm = { type: "", title: "", slug: "", description: "", photo: "", errors: [], attributes: [] };
      this.$modal.show("create-modal");
    },

    showUpdateModal(item) {
      this.updateForm = { id: item.id, type: item.key, title: item.title, slug: "", description: "", photo: "", errors: [], attributes: [] };
      this.selectedAttributes = item.related_attributes ?? [];
      this.filterAttributes("update");
      this.$modal.show("update-modal");
    },

    getPhotoStyle(item) {
      if (!item.photo) return "";
      return `background: url("${item.photo.url}") center/cover no-repeat;`;
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap   { display: flex; flex-direction: column; gap: 16px; }
.page-header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }
.page-header__right { display: flex; align-items: center; gap: 8px; }

.btn-primary-action   { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-primary-action:hover { opacity: .88; }
.btn-secondary-action { height: 36px; padding: 0 14px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-secondary-action:hover { border-color: var(--accent); color: var(--accent); }

/* ── Tags Grid ── */
.tags-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

.tag-card {
  border-radius: 12px;
  min-height: 140px;
  cursor: pointer;
  background: var(--bg-panel);
  border: 1px solid var(--border);
  overflow: hidden;
  transition: border-color .2s, transform .15s;
  background-size: cover !important;
  background-position: center !important;
}
.tag-card:hover { border-color: var(--accent); transform: translateY(-2px); }

.tag-card__overlay {
  width: 100%;
  height: 100%;
  min-height: 140px;
  background: rgba(0,0,0,.45);
  backdrop-filter: blur(1px);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: background .2s;
}
.tag-card:hover .tag-card__overlay { background: rgba(0,0,0,.65); }

/* fallback when no photo */
.tag-card:not([style]) .tag-card__overlay { background: var(--bg-hover); }
.tag-card:not([style]) .tag-card__title   { color: var(--text-1); }
.tag-card:not([style]) .tag-card__attr    { color: var(--text-3); }
.tag-card:not([style]) .tag-card__attrs-label { color: var(--text-3); }
.tag-card:not([style]) .tag-card__footer  { color: var(--text-3); }

.tag-card__header { display: flex; justify-content: flex-end; }
.tag-status { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 20px; font-size: 10px; font-weight: 600; }
.tag-status--active   { background: rgba(34,197,94,.2);  color: #4ade80; border: 1px solid rgba(34,197,94,.4); }
.tag-status--inactive { background: rgba(244,47,75,.2);  color: #fb7185; border: 1px solid rgba(244,47,75,.4); }

.tag-card__body   { flex: 1; }
.tag-card__title  { color: #fff; font-size: 14px; font-weight: 500; margin-bottom: 6px; }
.tag-card__attrs-label { font-size: 10px; color: rgba(255,255,255,.5); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 3px; }
.tag-card__attr   { font-size: 11px; color: rgba(255,255,255,.7); line-height: 1.5; }

.tag-card__footer { font-size: 11px; color: rgba(255,255,255,.4); display: flex; align-items: center; gap: 4px; }

/* ── Modal ── */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__footer { display: flex; align-items: center; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }

.form-field { display: flex; flex-direction: column; gap: 5px; }
.mb-3 { margin-bottom: .85rem; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; }
.required { color: var(--accent); }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }

.photo-uploaded { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #22c55e; }

/* Multiselect override */
.dd-multiselect >>> .multiselect__tags { background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; min-height: 36px; }
.dd-multiselect >>> .multiselect__input { background: transparent; color: var(--text-1); }
.dd-multiselect >>> .multiselect__single { color: var(--text-1); background: transparent; }
.dd-multiselect >>> .multiselect__placeholder { color: var(--text-3); }
.dd-multiselect >>> .multiselect__content-wrapper { background: var(--bg-panel); border: 1px solid var(--border); }
.dd-multiselect >>> .multiselect__option--highlight { background: var(--accent); color: #fff; }
.dd-multiselect >>> .multiselect__tag { background: var(--accent); border-radius: 20px; }
.dd-multiselect >>> .multiselect__tag-icon:hover { background: rgba(0,0,0,.3); }

.error-list { margin-top: 10px; padding-left: 16px; color: var(--accent); font-size: 13px; }

.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }
.btn-delete { height: 36px; padding: 0 14px; background: rgba(244,47,75,.1); border: 1px solid rgba(244,47,75,.3); border-radius: 8px; color: var(--accent); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background .15s; }
.btn-delete:hover { background: rgba(244,47,75,.2); }

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 1024px) { .tags-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 768px)  { .tags-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px)  { .tags-grid { grid-template-columns: 1fr; } }
</style>